import React, { useEffect, useState } from "react";
import { useApi } from "../../context/ApiContext";
import { useNavigate, useParams } from "react-router-dom";

const EMPTY = {
  name: "",
  slug: "",
  category: "Residential",
  status: "Rent",
  location: "",
  address: "",
  description: "",
  price: 0,
  agentName: "",
  agentEmail: "",
  agentPhone: "",
  agentWhatsapp: "",
  heroImage: "",
  gallery: [],
};

export default function PropertyForm({ mode = "create" }) {
  const isCreate = mode === "create";
  const navigate = useNavigate();
  const { slug: paramSlug } = useParams();
  const {
    adminGetProperty: fetchProperty,
    adminCreateProperty,
    adminUpdateProperty,
    adminUpload,
  } = useApi();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isCreate) return;
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchProperty(paramSlug);
        if (mounted) {
          const {
            name,
            slug,
            category,
            status,
            location,
            address,
            description,
            price = 0,
            agentName = "",
            agentEmail = "",
            agentPhone = "",
            agentWhatsapp = "",
            heroImage = "",
            gallery = [],
          } = data || {};
          setForm({
            name: name || "",
            slug: slug || "",
            category: category || "Residential",
            status: status || "Rent",
            location: location || "",
            address: address || "",
            description: description || "",
            price,
            agentName,
            agentEmail,
            agentPhone,
            agentWhatsapp,
            heroImage,
            gallery: Array.isArray(gallery) ? gallery : [],
          });
        }
      } catch (e) {
        if (mounted) setErr(e?.message || "Failed to load property");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, paramSlug]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === "price" ? Number(value || 0) : value }));
  }

  async function onUploadHero(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setSaving(true);
      const folder = form.slug ? `properties/${form.slug}` : "properties";
      const out = await adminUpload(file, { folder });
      setForm((f) => ({ ...f, heroImage: out.url }));
    } catch (e) {
      setErr(e?.message || "Hero upload failed");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  }

  async function onUploadGallery(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      setSaving(true);
      const folder = form.slug ? `properties/${form.slug}` : "properties";
      const uploaded = [];
      for (const f of files) {
        const out = await adminUpload(f, { folder });
        uploaded.push(out.url);
      }
      setForm((prev) => ({ ...prev, gallery: [...(prev.gallery || []), ...uploaded] }));
    } catch (e) {
      setErr(e?.message || "Gallery upload failed");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      if (isCreate) {
        await adminCreateProperty(form);
      } else {
        await adminUpdateProperty(paramSlug, form);
      }
      navigate("/admin/properties");
    } catch (e) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div>Loading…</div>;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold mb-4">{isCreate ? "New Property" : `Edit: ${form.name}`}</h1>

      <form onSubmit={onSubmit} className="space-y-5 bg-white border rounded-lg p-5">
        {err && <div className="text-red-600">{err}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Text name="name" label="Name" value={form.name} onChange={onChange} required />
          <Text name="slug" label="Slug" value={form.slug} onChange={onChange} required disabled={!isCreate} />
          <Select
            name="category"
            label="Category"
            value={form.category}
            onChange={onChange}
            options={["Residential", "Commercial", "Industrial"]}
          />
          <Select name="status" label="Status" value={form.status} onChange={onChange} options={["Rent", "Sale"]} />
          <Text name="location" label="Location" value={form.location} onChange={onChange} />
          <Text name="address" label="Address" value={form.address} onChange={onChange} />
          <NumberField name="price" label="Price" value={form.price} onChange={onChange} />
          <div>
            <Label>Hero Image</Label>
            {form.heroImage && (
              <div className="mb-2">
                <img src={form.heroImage} alt="hero" className="w-full max-h-48 object-cover rounded" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                name="heroImage"
                value={form.heroImage}
                onChange={onChange}
                placeholder="Paste image URL or upload"
                className="flex-1 border rounded-md px-3 py-2"
              />
              <label className="px-3 py-2 border rounded-md cursor-pointer bg-gray-50">
                Upload
                <input type="file" accept="image/*" onChange={onUploadHero} className="hidden" />
              </label>
            </div>
          </div>
        </div>

        <TextArea name="description" label="Description" value={form.description} onChange={onChange} />

        <div>
          <Label>Gallery</Label>
          {Array.isArray(form.gallery) && form.gallery.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
              {form.gallery.map((url, idx) => (
                <div key={`${url}_${idx}`} className="relative group">
                  <img src={url} alt="gallery" className="w-full h-28 object-cover rounded" />
                  <button
                    type="button"
                    className="absolute top-1 right-1 px-2 py-1 text-xs bg-white/90 border rounded-md opacity-0 group-hover:opacity-100"
                    onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((g) => g !== url) }))}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md bg-gray-50 cursor-pointer">
            Add Images
            <input type="file" accept="image/*" multiple onChange={onUploadGallery} className="hidden" />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Text name="agentName" label="Agent Name" value={form.agentName} onChange={onChange} />
          <Text name="agentEmail" label="Agent Email" value={form.agentEmail} onChange={onChange} />
          <Text name="agentPhone" label="Agent Phone" value={form.agentPhone} onChange={onChange} />
          <Text name="agentWhatsapp" label="Agent Whatsapp" value={form.agentWhatsapp} onChange={onChange} />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-md bg-black text-white disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 rounded-md border">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

/* small form bits */

function Label({ children }) {
  return <label className="block text-sm font-medium text-gray-700 mb-1">{children}</label>;
}
function Text(props) {
  const { label, ...rest } = props;
  return (
    <div>
      <Label>{label}</Label>
      <input {...rest} className="w-full border rounded-md px-3 py-2" />
    </div>
  );
}
function NumberField(props) {
  const { label, ...rest } = props;
  return (
    <div>
      <Label>{label}</Label>
      <input type="number" {...rest} className="w-full border rounded-md px-3 py-2" />
    </div>
  );
}
function TextArea(props) {
  const { label, ...rest } = props;
  return (
    <div>
      <Label>{label}</Label>
      <textarea rows={5} {...rest} className="w-full border rounded-md px-3 py-2" />
    </div>
  );
}
function Select({ label, options, ...rest }) {
  return (
    <div>
      <Label>{label}</Label>
      <select {...rest} className="w-full border rounded-md px-3 py-2">
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
